import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import { ArrowLeft, Save } from 'lucide-react';
import { tenantService } from '../../services';
import type { CompanyProfile, UpdateTenantRequest } from '../../types';

const CompanyEditPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<UpdateTenantRequest>({
    company_name: '',
    phone: '',
    email: '',
    address_line_1: '',
    address_line_2: '',
    country_id: undefined,
    state_id: undefined,
    city_id: undefined,
    postal_code: '',
    business_type_id: undefined,
    sub_business_id: undefined,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompany = async () => {
      setIsLoading(true);
      try {
        const data = await tenantService.getCompanyProfile();
        setFormData({
          company_name: data.company_name || '',
          phone: data.phone || '',
          email: data.email || '',
          address_line_1: data.address_line_1 || '',
          address_line_2: data.address_line_2 || '',
          country_id: data.country_id,
          state_id: data.state_id,
          city_id: data.city_id,
          postal_code: data.postal_code || '',
          business_type_id: data.business_type_id,
          sub_business_id: data.sub_business_id,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load company profile');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCompany();
  }, []);

  const handleInputChange = (field: keyof UpdateTenantRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await tenantService.updateCompanyProfile(formData);
      setSuccess('Company profile updated successfully!');
      setTimeout(() => navigate('/company'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update company profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-96">
          <Loading size="lg" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button variant="secondary" size="sm" onClick={() => navigate('/company')}>
                <ArrowLeft size={18} />
              </Button>
              <h1 className="text-2xl font-bold text-text-primary">Edit Company Profile</h1>
            </div>
            <p className="text-text-muted">Update your company information</p>
          </div>
        </div>

        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}

        {success && (
          <Alert variant="success" message={success} />
        )}

        <form onSubmit={handleSubmit}>
          <Card>
            <div className="p-6 space-y-6">
              {/* Company Information */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4">Company Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
                      placeholder="Enter company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
                      placeholder="company@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
                      placeholder="12345"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4">Address</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      value={formData.address_line_1}
                      onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                      className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
                      placeholder="Street address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={formData.address_line_2}
                      onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                      className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
                      placeholder="Suite, unit, building, floor, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-surface-secondary">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/company')}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </form>
      </div>
    </MainLayout>
  );
};

export default CompanyEditPage;
